import type { AuditLogEvent } from '../../../domain/audit-log-event'

import { CheckOutlined, CopyOutlined } from '@ant-design/icons'
import { Button, Card, Space, Typography } from 'antd'
import cx from 'classnames'
import hljs from 'highlight.js/lib/core'
import { useState } from 'react'

import { AuditLogAction } from '../../../domain/audit-log-action.enum'
import { AuditLogResourceType } from '../../../domain/audit-log-resource-type.enum'
import * as constants from '../constants'

interface Props {
  auditLog: AuditLogEvent
}

export const CheckRunRerunExpandableRow = (props: Props): JSX.Element | null => {
  const [shouldShowAllIds, setShouldShowAllIds] = useState(false)
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [copiedAll, setCopiedAll] = useState(false)

  const auditLogResource = props.auditLog.resource
  const auditLogResourceType = auditLogResource?.type
  const auditLogAction = props.auditLog.action

  if (
    auditLogResourceType !== AuditLogResourceType.CheckRun ||
    auditLogAction !== AuditLogAction.Rerun
  ) {
    return null
  }

  const checkRunIds = auditLogResource?.data?.ids ?? []

  const copyTextToClipboard = async (text: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text)

      setCopiedId(text)
      setTimeout(() => {
        setCopiedId(null)
      }, 2000)
    } catch (err) {
      console.error('Failed to copy text:', err)
    }
  }

  const copyAllIds = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(checkRunIds.join('\n') ?? '')

      setCopiedAll(true)
      setTimeout(() => {
        setCopiedAll(false)
      }, 2000)
    } catch (err) {
      console.error('Failed to copy all IDs:', err)
    }
  }

  const handleCopyClick = (id: string) => (): void => {
    void copyTextToClipboard(id)
  }

  const handleShowAllIdsClick = (): void => {
    setShouldShowAllIds(!shouldShowAllIds)
  }

  const handleCopyAllClick = (): void => {
    void copyAllIds()
  }

  const displayedIds = shouldShowAllIds ? checkRunIds : checkRunIds.slice(0, 4)
  const hasMoreIds = checkRunIds.length > 4

  const summary = {
    resetTotalsChecked: auditLogResource?.data.resetTotalsChecked,
    rebuildCheckRegisterReports: auditLogResource?.data.rebuildCheckRegisterReports,
    dropOffToLin: auditLogResource?.data.dropOffToLin,
    emailNotification: auditLogResource?.data.emailNotification
  }

  const highlightedResource = hljs.highlight(JSON.stringify(auditLogResource, null, 2), {
    language: 'json'
  }).value

  return (
    <div className='p-6'>
      <div className='space-y-6'>
        <div>
          <Typography.Title level={4}>Payload</Typography.Title>

          <Card>
            {Object.entries(summary).map(([key, value]) => (
              <div key={key} className='flex items-center space-x-2'>
                <Typography.Text strong>
                  {constants.AUDIT_LOG_CHECK_RUN_RERUN_SUMMARY_MAPPER[key as keyof typeof summary]}:
                </Typography.Text>
                <Typography.Text>{value ? '✅' : '❌'}</Typography.Text>
              </div>
            ))}

            {auditLogResource?.data?.reason && (
              <div className='flex items-center space-x-2'>
                <Typography.Text strong>Reason:</Typography.Text>
                <Typography.Text>{auditLogResource?.data?.reason}</Typography.Text>
              </div>
            )}
          </Card>
        </div>

        <div>
          <Typography.Title level={4}>IDs</Typography.Title>

          <Card>
            <div
              className={cx('grid gap-2 mb-3', {
                'grid-cols-1': checkRunIds.length === 1,
                'grid-cols-2': checkRunIds.length > 1
              })}
            >
              {displayedIds.map(id => (
                <div
                  key={id}
                  className='flex justify-between items-center p-2 rounded border transition-colors cursor-pointer hover:bg-blue-50 group'
                  onClick={handleCopyClick(id)}
                  title='Click to copy'
                >
                  <Typography.Text className='flex-1 mr-2 truncate'>{id}</Typography.Text>

                  <div className='flex flex-shrink-0 items-center space-x-2'>
                    {copiedId === id ? (
                      <CheckOutlined className='text-green-600' />
                    ) : (
                      <CopyOutlined className='text-gray-400 transition-colors group-hover:text-blue-600' />
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className='flex justify-between items-center'>
              <Typography.Text type='secondary'>
                Showing {displayedIds.length} of {checkRunIds.length} IDs
              </Typography.Text>

              <Space>
                {hasMoreIds && (
                  <Button onClick={handleShowAllIdsClick}>
                    {shouldShowAllIds ? 'Show Less' : 'Expand All'}
                  </Button>
                )}

                <Button onClick={handleCopyAllClick}>
                  {copiedAll ? 'Copied All!' : 'Copy All'}
                </Button>
              </Space>
            </div>
          </Card>
        </div>

        <div>
          <Typography.Title level={4}>Resource</Typography.Title>

          <pre
            className='overflow-auto p-4 max-h-96 text-xs bg-white rounded border'
            dangerouslySetInnerHTML={{ __html: highlightedResource }}
          />
        </div>
      </div>
    </div>
  )
}
